"use client";

import React, { useEffect, useState } from "react";

import { useForm, useFieldArray } from "react-hook-form";

import {

  IconX,

  IconRefresh,

  IconLayersIntersect,

  IconCheck,

  IconArrowBackUp,

  IconPlus,

} from "@tabler/icons-react";

import toast from "react-hot-toast";

import {

  useFetchProductManifestViewData,

  useProductManifestAddInventoryMutation,

  useFetchMerchantLocations,

} from "../../../apis/productManifestApis";

import { useLocationBarcodeVerifyMutation } from "../../../hooks/inventoryHooks";

import { useVerifySkuMutation } from "../../../apis/productApis";



type ProductForm = {

  products: {

    scannedLocation?: string;

    sku?: string;

    quantity?: number;

    expiryDate?: string | null;

    hasExpiry?: boolean;

    productId?: string;

    productName?: string;

    inventoryType?: "received" | "reserved";

  }[];

};



type SuggestionObj = {

  id: string;

  barcodeLabel: string;

  currentInventoryCount: number;

  warehouseId: string;

};



type Props = {

  open: boolean;

  onClose: () => void;

  id?: string;

  fetchData: () => void;

  isSidebar?: boolean;

};



type WarehouseOption = { warehouseId: string; warehouseName: string };



const AddProductInventoryModal = ({ open, onClose, id, fetchData, isSidebar }: Props) => {

  const [scanningSteps, setScanningSteps] = useState<string[]>([]);

  const [isVerifyingBarcode, setIsVerifyingBarcode] = useState<boolean[]>([]);

  const [isVerifyingSku, setIsVerifyingSku] = useState<boolean[]>([]);

  const [capacityWarnings, setCapacityWarnings] = useState<string[]>([]);

  // Updated to store objects instead of strings

  const [suggestions, setSuggestions] = useState<(SuggestionObj[] | null)[]>(

    [],

  );

  const [palletSuggestions, setPalletSuggestions] = useState<

    (SuggestionObj[] | null)[]

  >([]);

  const [storageMeta, setStorageMeta] = useState<
    { binCap: number; palletCap: number; supportsBins?: boolean; supportsPallets?: boolean }[]
  >([]);

  const [confirmingPallet, setConfirmingPallet] = useState<number | null>(null);

  const [userChoice, setUserChoice] = useState<(boolean | null)[]>([]);

  const [showSkuDropdown, setShowSkuDropdown] = useState<boolean[]>([]);

  const [activeSkuIndex, setActiveSkuIndex] = useState<number | null>(null);

  const [warehouseOptions, setWarehouseOptions] = useState<(WarehouseOption[] | null)[]>([]);

  const [selectedWarehouseIds, setSelectedWarehouseIds] = useState<(string | null)[]>([]);

  // Tracks which rows were auto-distributed (value = parent row index, -1 = original row)

  const [distributedFromIndex, setDistributedFromIndex] = useState<number[]>([]);

  // Tracks verified location IDs for each row

  const [verifiedLocationIds, setVerifiedLocationIds] = useState<(string | null)[]>([]);



  // Removed global inventoryType state

  // const [inventoryType, setInventoryType] = useState<"received" | "reserved">("received");



  const { data, refetch: fetchDetails } = useFetchProductManifestViewData(id);

  const { mutate: verifyLocationBarcode } = useLocationBarcodeVerifyMutation();

  const { mutate: verifySkuBarcode } = useVerifySkuMutation();

  const { mutate: addInventory, isPending } =

    useProductManifestAddInventoryMutation(id || "");

  const [activeLocationIndex, setActiveLocationIndex] = useState<number | null>(

    null,

  );



  const { data: locationsRec } = useFetchMerchantLocations(data?.forClientId, "received");

  const { data: locationsRes } = useFetchMerchantLocations(data?.forClientId, "reserved");

  console.log(locationsRec, locationsRes, "l");



  const { register, handleSubmit, watch, setValue, setFocus, control, reset } =

    useForm<ProductForm>({

      defaultValues: { products: [] },

    });



  const { fields, append, remove } = useFieldArray({

    control,

    name: "products",

  });



  const watchedProducts = watch("products");

  console.log("DEBUG - watchedProducts:", watchedProducts);

  console.log("DEBUG - scanningSteps:", scanningSteps);

  console.log("DEBUG - isVerifyingSku:", isVerifyingSku);

  console.log("DEBUG - capacityWarnings:", capacityWarnings);



  useEffect(() => {

    if (id) fetchDetails();

  }, [id, fetchDetails]);



  useEffect(() => {

    if (open && data?.products) {

      // Only initialize if form is empty (initial load), not during auto distribute

      const currentProducts = watch("products");

      if (currentProducts && currentProducts.length > 0) {

        return; // Don't reset existing state during auto distribute

      }



      // Always start with just one empty row

      const count = 1;



      setScanningSteps(new Array(count).fill("sku"));

      setIsVerifyingBarcode(new Array(count).fill(false));

      setIsVerifyingSku(new Array(count).fill(false));

      setCapacityWarnings(new Array(count).fill(""));

      setSuggestions(new Array(count).fill(null));

      setPalletSuggestions(new Array(count).fill(null));

      setUserChoice(new Array(count).fill(null));

      setShowSkuDropdown(new Array(count).fill(false));

      setStorageMeta(new Array(count).fill({ binCap: 9999, palletCap: 9999, supportsBins: true, supportsPallets: true }));

      setWarehouseOptions(new Array(count).fill(null));

      setSelectedWarehouseIds(new Array(count).fill(null));

      setDistributedFromIndex(new Array(count).fill(-1));

      setVerifiedLocationIds(new Array(count).fill(null));



      // Create one empty row

      const initialRows = [{

        productId: "",

        scannedLocation: "",

        sku: "",

        quantity: 0,

        expiryDate: "",

        hasExpiry: false,

        productName: "",

        inventoryType: undefined,

      }];



      setValue("products", initialRows);

    }

  }, [data, setValue, watch, open]);



  const handleModalClose = () => {

    // Reset form data

    reset({ products: [] });



    // Reset all custom states

    setScanningSteps([]);

    setIsVerifyingBarcode([]);

    setIsVerifyingSku([]);

    setCapacityWarnings([]);

    setSuggestions([]);

    setPalletSuggestions([]);

    setStorageMeta([]);

    setConfirmingPallet(null);

    setUserChoice([]);

    setShowSkuDropdown([]);

    setActiveSkuIndex(null);

    setActiveLocationIndex(null);

    setWarehouseOptions([]);

    setSelectedWarehouseIds([]);

    setVerifiedLocationIds([]);



    // Call the original onClose handler

    onClose();

  };



  const handleVerifyBarcode = (barcode: string, index: number, warehouseId?: string, locationId?: string) => {

    setIsVerifyingBarcode((p) => {

      const n = [...p];

      n[index] = true;

      return n;

    });

    const matchedProductForBarcode = data?.products?.find(
      (p: any) => p.sku === watch(`products.${index}.sku`)
    );

    verifyLocationBarcode(

      {

        barcodeId: barcode,

        inventoryType: watch(`products.${index}.inventoryType`),

        expiryDate: watch(`products.${index}.expiryDate`) || matchedProductForBarcode?.expiryDate || null,

        ...(warehouseId ? { warehouseId } : {}),

        ...(locationId ? { locationId } : {})

      } as any,

      {

        onSuccess: (res: any) => {

          // ── Multiple warehouses: let the user pick one ──

          if (res?.multipleWarehouses) {

            setWarehouseOptions((p) => {

              const n = [...p];

              n[index] = res.warehouses;

              return n;

            });

            // Keep location field populated with the barcode label

            setValue(`products.${index}.scannedLocation`, res.barcodeLabel || barcode);

            setIsVerifyingBarcode((p) => { const n = [...p]; n[index] = false; return n; });

            return;

          }



          // ── Single warehouse: clear any previous options ──

          setWarehouseOptions((p) => { const n = [...p]; n[index] = null; return n; });

          setSelectedWarehouseIds((p) => { const n = [...p]; n[index] = warehouseId || null; return n; });

          setVerifiedLocationIds((p) => { const n = [...p]; n[index] = locationId || null; return n; });



          const barcodeLabel = res?.barcodeLabel || barcode;

          const availableQty = parseInt(res?.availableQty ?? "0");

          const locType = res?.locationType || "";



          setValue(`products.${index}.scannedLocation`, barcodeLabel);



          // ── Capacity check ──

          const meta = storageMeta[index];

          if (meta) {

            const unitCap = locType === "PALLETS" ? meta.palletCap : meta.binCap;

            const qty = parseInt(watch(`products.${index}.quantity`) as any) || 0;

            const freeSpace = unitCap - availableQty;



            setUserChoice((p) => {

              const n = [...p];

              n[index] = locType === "PALLETS";

              return n;

            });



            if (unitCap > 0 && qty > freeSpace) {

              setCapacityWarnings((p) => {

                const n = [...p];

                n[index] = `Only ${Math.max(freeSpace, 0)} space available at "${barcodeLabel}". Select another location below.`;

                return n;

              });



              if (locType !== "PALLETS" && meta.supportsPallets) {

                setConfirmingPallet(index);

              } else {

                setConfirmingPallet(null);

              }



              // Do NOT advance to "item" — keep location active so suggestions stay visible

              return;

            }

          }



          // ── Capacity OK: location verification complete ──
          setUserChoice((p) => { const n = [...p]; n[index] = locType === "PALLETS"; return n; });
          setCapacityWarnings((p) => { const n = [...p]; n[index] = ""; return n; });
          // Don't change scanning step - keep it as "location" to indicate process is complete
          toast.success("Location verified");

          if (watch(`products.${index}.inventoryType`) === "received") {
            setTimeout(() => setFocus(`products.${index}.quantity`), 100);
          }
        },

        onError: (err) => {

          setValue(`products.${index}.scannedLocation`, "");

          toast.error(String(err));

        },

        onSettled: () =>

          setIsVerifyingBarcode((p) => {

            const n = [...p];

            n[index] = false;

            return n;

          }),

      },

    );

  };



  // Called when user picks a warehouse from the dropdown

  const handleWarehouseSelect = (warehouseId: string, index: number) => {

    setSelectedWarehouseIds((p) => { const n = [...p]; n[index] = warehouseId; return n; });

    setWarehouseOptions((p) => { const n = [...p]; n[index] = null; return n; });

    // Re-verify barcode now that we know the warehouse

    const barcode = watch(`products.${index}.scannedLocation`);

    if (barcode) handleVerifyBarcode(barcode, index, warehouseId);

  };



  const handleSkuSelection = (sku: string, index: number, product: any) => {

    setValue(`products.${index}.sku`, sku);

    setValue(`products.${index}.productId`, product.productId);

    setValue(`products.${index}.productName`, product.productName || "");

    setShowSkuDropdown((p) => {

      const n = [...p];

      n[index] = false;

      return n;

    });

    setActiveSkuIndex(null);



    // Check if this SKU has already been processed in existing rows

    const existingProcessedRows = watchedProducts.filter((p, i) =>

      i !== index && p.sku === sku && (p.inventoryType === "received" || p.inventoryType === "reserved")

    );



    if (existingProcessedRows.length > 0) {

      // SKU already processed - pre-select opposite type

      const lastProcessedRow = existingProcessedRows[existingProcessedRows.length - 1];

      const oppositeType = lastProcessedRow.inventoryType === "received" ? "reserved" : "received";



      const totalQty = Number(product.receivedQty) || 0;

      const processedQty = existingProcessedRows.reduce((sum, p) => sum + (Number(p.quantity) || 0), 0);

      const remainingQty = totalQty - processedQty;



      setValue(`products.${index}.inventoryType`, oppositeType);
      const resolvedQty = remainingQty > 0 ? remainingQty : 0;
      if (oppositeType === "reserved") {
        setValue(`products.${index}.quantity`, resolvedQty);
        setScanningSteps((p) => {
          const n = [...p];
          n[index] = "quantity";
          return n;
        });
        setTimeout(() => setFocus(`products.${index}.quantity`), 100);
      } else {
        setValue(`products.${index}.quantity`, 0);
        setScanningSteps((p) => {
          const n = [...p];
          n[index] = "location";
          return n;
        });
        handleVerifySku(sku, index, "received", resolvedQty);
        setTimeout(() => setFocus(`products.${index}.scannedLocation`), 100);
      }

    } else {

      // First time processing this SKU - go to type selection

      setScanningSteps((p) => {

        const n = [...p];

        n[index] = "type";

        return n;

      });

      // Trigger SKU verification after selection

      handleVerifySku(sku, index);

    }

  };



  const handleTypeSelection = (type: "received" | "reserved", index: number) => {

    // Find the product in manifest to get the correct quantity for this type

    const sku = watch(`products.${index}.sku`);

    const matchedProduct = data?.products?.find((p: any) => p.sku === sku);



    if (matchedProduct) {

      // Calculate total available quantity (receivedQty)

      const totalAvailable = Number(matchedProduct.receivedQty) || 0;



      // Calculate already processed quantities for this SKU

      const existingProcessedRows = watchedProducts.filter((p, i) =>

        i !== index && p.sku === sku && (p.inventoryType === "received" || p.inventoryType === "reserved")

      );



      const processedQty = existingProcessedRows.reduce((sum, p) => sum + (Number(p.quantity) || 0), 0);



      // Calculate remaining quantity

      const remainingQty = totalAvailable - processedQty;



      setValue(`products.${index}.inventoryType`, type);
      const resolvedQty = remainingQty > 0 ? remainingQty : 0;
      if (type === "reserved") {
        setValue(`products.${index}.quantity`, resolvedQty);
        setScanningSteps((p) => {
          const n = [...p];
          n[index] = "quantity";
          return n;
        });
        setTimeout(() => setFocus(`products.${index}.quantity`), 100);
      } else {
        setValue(`products.${index}.quantity`, 0);
        setScanningSteps((p) => {
          const n = [...p];
          n[index] = "location";
          return n;
        });
        if (sku) {
          handleVerifySku(sku, index, "received", resolvedQty);
        }
        setTimeout(() => setFocus(`products.${index}.scannedLocation`), 100);
      }
    }

  };



  const handleVerifySku = (
    barcode: string,
    index: number,
    explicitInventoryType?: string,
    explicitQty?: number
  ) => {

    console.log("DEBUG - Verifying SKU:", barcode);

    console.log("DEBUG - Available products:", data?.products);

    console.log("DEBUG - First product structure:", data?.products?.[0]);



    const matchedProduct = data?.products?.find((p: any) => {

      const isMatch = p.sku === barcode && !p.isAddedToInventory;

      // We don't filter by qty here anymore because type isn't selected yet in the first call

      return isMatch;

    });



    console.log("DEBUG - Matched product:", matchedProduct);



    if (!matchedProduct) {

      setValue(`products.${index}.productId`, "");

      console.log("DEBUG - SKU not found in manifest");

      return toast.error("SKU does not belong to this manifest.");

    }



    setIsVerifyingSku((p) => {

      const n = [...p];

      n[index] = true;

      return n;

    });



    // Collect all already used location IDs from all rows (except current row)

    // Only use verified location IDs (UUIDs), not barcode labels

    const usedLocationIds = verifiedLocationIds

      .filter((locationId, idx): locationId is string => idx !== index && locationId !== undefined && locationId !== null);



    // Debug logging

    console.log("DEBUG - Excluding locations:", {

      verifiedLocationIds,

      watchedProducts,

      usedLocationIds

    });



    // Use explicitly passed type (e.g. from handleTypeSelection) to avoid race condition

    // where setValue hasn't propagated before watch() is called

    const resolvedInventoryType = explicitInventoryType || watch(`products.${index}.inventoryType`);



    verifySkuBarcode(
      {
        sku: barcode,
        location: watch(`products.${index}.scannedLocation`) as any,
        qty: explicitQty !== undefined ? explicitQty : (watch(`products.${index}.quantity`) as any),
        inventoryType: resolvedInventoryType,
        warehouseId: selectedWarehouseIds[index] || undefined,
        excludeLocationIds: usedLocationIds,
        expiryDate: matchedProduct?.expiryDate || watch(`products.${index}.expiryDate`) || undefined,
      },

      {

        onSuccess: (res: any) => {

          console.log("DEBUG - verifySkuBarcode API success:", res);

          if (res?.multipleWarehouses) {

            setWarehouseOptions((p) => {

              const n = [...p];

              n[index] = res.warehouses;

              return n;

            });

            setValue(`products.${index}.scannedLocation`, res.barcodeLabel || "");

            return;

          }



          // Handle storage data that might be string or array

          let storageArray = [];

          if (res?.storage) {

            if (typeof res.storage === "string") {

              try {

                storageArray = [JSON.parse(res.storage)];

              } catch (e) {

                console.error("Failed to parse storage string:", e);

              }

            } else if (Array.isArray(res.storage)) {

              storageArray = res.storage;

            } else if (typeof res.storage === "object") {

              storageArray = [res.storage];

            }

          }



          console.log("DEBUG - Processed storage array:", storageArray);



          const binStorage = storageArray?.find(

            (s: any) => s?.type === "BINS" || s?.storageType === "BINS"

          );

          const palletStorage = storageArray?.find(

            (s: any) => s?.type === "PALLETS" || s?.storageType === "PALLETS"

          );



          const binCap = binStorage ? parseInt(binStorage.capacity) || 0 : 9999;

          const palletCap = palletStorage ? parseInt(palletStorage.capacity) || 0 : 9999;



          const availableQty = parseInt(res?.availableQty || "0", 10);

          const unitCapacity =

            res?.locationType === "PALLETS" ? palletCap : binCap;



          const freeSpace = unitCapacity - availableQty;



          // Always set suggestions from response

          setSuggestions((p) => {

            const n = [...p];

            n[index] = res?.suggestedBins || [];

            return n;

          });

          setPalletSuggestions((p) => {

            const n = [...p];

            n[index] = res?.suggestedPallets || [];

            return n;

          });



          setStorageMeta((p) => {

            const n = [...p];

            n[index] = { binCap, palletCap, supportsBins: !!binStorage, supportsPallets: !!palletStorage };

            return n;

          });



          const currentQty = watch(`products.${index}.quantity`) || 0;

          const scannedLocation = watch(`products.${index}.scannedLocation`);



          if (scannedLocation) {

            setUserChoice((p) => {

              const n = [...p];

              n[index] = res?.locationType === "PALLETS";

              return n;

            });

          }



          if (scannedLocation && unitCapacity > 0 && currentQty > freeSpace) {

            setCapacityWarnings((p) => {

              const n = [...p];

              n[index] = `Only ${freeSpace > 0 ? freeSpace : 0} space available here.`;

              return n;

            });

            if (res?.locationType !== "PALLETS" && !!palletStorage) {

              setConfirmingPallet(index);

            } else {

              setConfirmingPallet(null);

            }

          } else {

            setCapacityWarnings((p) => {

              const n = [...p];

              n[index] = "";

              return n;

            });

          }



          setValue(`products.${index}.sku`, barcode);

          setValue(`products.${index}.productId`, matchedProduct.productId);

          setValue(

            `products.${index}.productName`,

            matchedProduct.productName || "",

          );

          setValue(

            `products.${index}.expiryDate`,

            matchedProduct.expiryDate || "",

          );

          console.log("DEBUG - SKU verified, productId set:", matchedProduct.productId);

          console.log("DEBUG - Updated watchedProducts after SKU verification:", watch("products"));

          toast.success(`SKU Verified`);

        },

        onError: (err: any) => {

          console.log("DEBUG - verifySkuBarcode API error:", err);

          toast.error(String(err));

        },

        onSettled: () =>

          setIsVerifyingSku((p) => {

            const n = [...p];

            n[index] = false;

            return n;

          }),

      },

    );

  };



  const handleBulkSplit = (index: number) => {

    const currentQty = watch(`products.${index}.quantity`) || 0;

    const isPalletMode = !!userChoice[index];

    const availableTargets = isPalletMode

      ? palletSuggestions[index] || []

      : suggestions[index] || [];

    const unitCap = isPalletMode

      ? storageMeta[index].palletCap

      : storageMeta[index].binCap;



    if (availableTargets.length === 0)

      return toast.error("No suggestions available.");



    const productInfo = {

      sku: watch(`products.${index}.sku`),

      productId: watch(`products.${index}.productId`),

      productName: watch(`products.${index}.productName`),

      expiryDate: watch(`products.${index}.expiryDate`),

    };



    // Get current bin locations to prevent duplicates

    const currentBinLocations = watchedProducts

      .map(p => p.scannedLocation)

      .filter(location => location && location.trim() !== "");



    let remaining = currentQty;

    let targetIdx = 0;



    const freeSpaceMatch = capacityWarnings[index]?.match(/\d+/);

    const freeSpaceInCurrentBin = freeSpaceMatch

      ? parseInt(freeSpaceMatch[0])

      : 0;



    if (freeSpaceInCurrentBin > 0) {

      const takeFirst = Math.min(remaining, freeSpaceInCurrentBin);

      setValue(`products.${index}.quantity`, takeFirst);

      remaining -= takeFirst;

    } else {

      const target = availableTargets[0];

      const targetSpace = unitCap - (target.currentInventoryCount || 0);

      const takeFirst = Math.min(remaining, targetSpace);

      setValue(`products.${index}.scannedLocation`, target.barcodeLabel);

      setValue(`products.${index}.quantity`, takeFirst);

      remaining -= takeFirst;

      targetIdx = 1;

    }



    if (remaining > 0) {

      const newSteps: string[] = [];

      const newWarnings: string[] = [];

      const newMeta: any[] = [];

      const newLocationIds: (string | null)[] = [];

      const newWarehouseIds: (string | null)[] = [];



      while (remaining > 0 && targetIdx < availableTargets.length) {

        const target = availableTargets[targetIdx];

        const targetFreeSpace = unitCap - (target.currentInventoryCount || 0);



        // Skip if this bin location already exists in current rows

        if (currentBinLocations.includes(target.barcodeLabel)) {

          targetIdx++;

          continue;

        }



        if (targetFreeSpace > 0) {

          const takeQty = Math.min(remaining, targetFreeSpace);

          const parentInventoryType = watch(`products.${index}.inventoryType`);

          append({

            ...productInfo,

            inventoryType: parentInventoryType,

            scannedLocation: target.barcodeLabel,

            quantity: takeQty,

          });

          newSteps.push("location");

          newWarnings.push("");

          newMeta.push({ ...storageMeta[index] });

          // Store the location ID and warehouse ID for this new row

          newLocationIds.push(target.id);

          newWarehouseIds.push(target.warehouseId);

          remaining -= takeQty;

        }

        targetIdx++;

      }



      setScanningSteps((p) => [...p, ...newSteps]);

      setCapacityWarnings((p) => {

        const n = [...p, ...newWarnings];

        n[index] = "";

        return n;

      });

      setStorageMeta((p) => [...p, ...newMeta]);

      setShowSkuDropdown((p) => [...p, ...new Array(newSteps.length).fill(false)]);

      setIsVerifyingBarcode((p) => [

        ...p,

        ...new Array(newSteps.length).fill(false),

      ]);

      setIsVerifyingSku((p) => [

        ...p,

        ...new Array(newSteps.length).fill(false),

      ]);

      setWarehouseOptions((p) => [...p, ...new Array(newSteps.length).fill(null)]);

      setSelectedWarehouseIds((p) => [...p, ...newWarehouseIds]);

      setVerifiedLocationIds((p) => [...p, ...newLocationIds]);

      // Mark these rows as distributed from `index`

      setDistributedFromIndex((p) => [...p, ...new Array(newSteps.length).fill(index)]);

    } else {

      setCapacityWarnings((p) => {

        const n = [...p];

        n[index] = "";

        return n;

      });

    }



    setConfirmingPallet(null);

    toast.success("Inventory distributed.");

  };



  const handleClearRow = (index: number) => {

    const originalCount =

      data?.products?.filter((p: any) => !p?.isAddedToInventory).length || 0;

    if (fields.length > originalCount) {

      remove(index);

      setScanningSteps((p) => p.filter((_, i) => i !== index));

      setCapacityWarnings((p) => p.filter((_, i) => i !== index));

      setStorageMeta((p) => p.filter((_, i) => i !== index));

      setShowSkuDropdown((p) => p.filter((_, i) => i !== index));

      setWarehouseOptions((p) => p.filter((_, i) => i !== index));

      setSelectedWarehouseIds((p) => p.filter((_, i) => i !== index));

      setVerifiedLocationIds((p) => p.filter((_, i) => i !== index));

      setDistributedFromIndex((p) => p.filter((_, i) => i !== index));

    } else {

      setValue(`products.${index}.scannedLocation`, "");

      setValue(`products.${index}.sku`, "");

      setValue(`products.${index}.productId`, "");

      setValue(`products.${index}.inventoryType`, "" as any);

      setValue(`products.${index}.quantity`, "" as any);

      setValue(`products.${index}.productName`, "");

      setValue(`products.${index}.expiryDate`, "" as any);

      setCapacityWarnings((p) => {

        const n = [...p];

        n[index] = "";

        return n;

      });

      setScanningSteps((p) => {

        const n = [...p];

        n[index] = "sku";

        return n;

      });

      setShowSkuDropdown((p) => {

        const n = [...p];

        n[index] = false;

        return n;

      });

      setWarehouseOptions((p) => { const n = [...p]; n[index] = null; return n; });

      setSelectedWarehouseIds((p) => { const n = [...p]; n[index] = null; return n; });

      setVerifiedLocationIds((p) => { const n = [...p]; n[index] = null; return n; });

    }

  };



  const onSubmit = (formData: ProductForm) => {

    // Validate per SKU

    const skuMap: Record<string, { received: number; reserved: number; total: number }> = {};



    // Initialize skuMap with expected total quantities from manifest

    data?.products?.forEach((p: any) => {

      skuMap[p.sku] = { received: 0, reserved: 0, total: Number(p.receivedQty) || 0 };

    });



    // Sum up the quantities entered in the form

    for (const p of formData.products) {

      if (!p.sku || p.quantity === undefined || p.quantity === null) continue;



      const qty = Number(p.quantity);

      if (qty < 0) {

        return toast.error(`Quantity cannot be less than zero for SKU ${p.sku}`);

      }



      if (skuMap[p.sku]) {

        if (p.inventoryType === "received") {

          skuMap[p.sku].received += qty;

        } else if (p.inventoryType === "reserved") {

          skuMap[p.sku].reserved += qty;

        }

      }

    }



    // Check constraints

    for (const [sku, counts] of Object.entries(skuMap)) {

      if (counts.received > counts.total) {

        return toast.error(`RECEIVED quantity (${counts.received}) cannot be more than total quantity (${counts.total}) for SKU ${sku}`);

      }

      if (counts.reserved > counts.total) {

        return toast.error(`RESERVED quantity (${counts.reserved}) cannot be more than total quantity (${counts.total}) for SKU ${sku}`);

      }

      if (counts.received + counts.reserved > counts.total) {

        return toast.error(`RECEIVED + RESERVED quantity (${counts.received + counts.reserved}) cannot be more than total quantity (${counts.total}) for SKU ${sku}`);

      }

    }



    const payload = formData.products

      .map((p, i) =>

        !p?.scannedLocation || !p?.sku || capacityWarnings[i]

          ? null

          : {

            ...p,

            expiryDate: p.expiryDate === "" ? null : p.expiryDate,

            scannedLocationId: verifiedLocationIds[i] || null,

            scannedLocationWarehouseId: selectedWarehouseIds[i] || null,

          },

      )

      .filter(Boolean);



    if (payload.length === 0) return toast.error("No valid products to add.");

    addInventory(

      { products: payload as any }, // Removed global inventoryType

      {

        onSuccess: () => {

          toast.success("Inventory added");

          fetchData();

          fetchDetails();

          onClose();

        },

      },

    );

  };



  // Check for duplicate locations across different SKUs (regardless of inventory type)

  const hasDuplicateLocations = watchedProducts.some((product, index) => {

    if (!product.scannedLocation) return false;



    return watchedProducts.some((otherProduct, otherIndex) =>

      index !== otherIndex &&

      otherProduct.scannedLocation === product.scannedLocation

    );

  });



  const isFormDisabled =

    watchedProducts.length === 0 ||

    watchedProducts.some(

      (p, i) =>

        !p.productId || !p.scannedLocation || capacityWarnings[i] !== "",

    ) ||

    hasDuplicateLocations;



  console.log("DEBUG - isFormDisabled calculation:");

  console.log("  - watchedProducts.length:", watchedProducts.length);

  console.log("  - watchedProducts:", watchedProducts);

  console.log("  - capacityWarnings:", capacityWarnings);

  watchedProducts.forEach((p, i) => {

    console.log(`  - Row ${i}: productId=${!!p.productId}, scannedLocation=${!!p.scannedLocation}, hasWarning=${capacityWarnings[i] !== ""}`);

  });

  console.log("  - Final isFormDisabled:", isFormDisabled);



  // Check if all SKU combinations are processed (received + reserved for each SKU)

  const allCombinationsProcessed = data?.products?.every((product: any) => {

    if (product.isAddedToInventory) return true; // Already processed in inventory



    // Check if this SKU has been processed

    const processedRows = watchedProducts.filter((wp) =>

      wp.sku === product.sku &&

      (wp.inventoryType === "received" || wp.inventoryType === "reserved")

    );



    // SKU is fully processed if its total quantity is met

    const totalQty = Number(product.receivedQty) || 0;

    const processedQty = processedRows.reduce((sum, wp) => sum + (Number(wp.quantity) || 0), 0);

    return processedQty >= totalQty;

  }) || false;



  // Check if there are any available SKUs for dropdown

  const availableSkus = data?.products?.filter((product: any) => {

    if (product.isAddedToInventory) return false;



    // Check if this SKU has already been processed in the form

    const processedRows = watchedProducts.filter((wp) =>

      wp.sku === product.sku &&

      (wp.inventoryType === "received" || wp.inventoryType === "reserved")

    );



    // SKU is available if its processed quantity is less than total

    const quantity = Number(product.receivedQty) || 0;

    const processedQty = processedRows.reduce((sum, wp) => sum + (Number(wp.quantity) || 0), 0);

    return processedQty < quantity && quantity > 0;

  }) || [];



  console.log("DEBUG - data?.products:", data?.products);

  console.log("DEBUG - availableSkus:", availableSkus);



  const hasAvailableSkus = availableSkus.length > 0;
  console.log("DEBUG - hasAvailableSkus:", hasAvailableSkus);

  const anyRowHasQuantity = watchedProducts?.some((p, idx) => {
    const showQuantityInput =
      p.inventoryType === "reserved" ||
      (p.inventoryType === "received" && !!p.scannedLocation);
    return p.productId && showQuantityInput;
  });

  const anyRowHasLocation = watchedProducts?.some((p, idx) => {
    const showLocationInput =
      p.inventoryType === "received" ||
      (p.inventoryType === "reserved" && scanningSteps[idx] === "location");
    return p.productId && showLocationInput;
  });

  if (!open || !id) return null;



  const formContent = (
    <form
      onSubmit={handleSubmit(onSubmit)}
      onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
      className={isSidebar ? "w-full h-full flex flex-col" : "rounded-2xl relative h-full flex flex-col [@media(min-width:1536px)]:w-9/12"}
    >
      {!isSidebar && (
        <div className="flex items-center justify-between">
          <div className="flex flex-col w-full px-6 py-4 rounded-tl-3xl rounded-tr-[63px] bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC]">
            <h1 className="text-xl font-medium">Add To Inventory</h1>
          </div>
          <button
            type="button"
            onClick={handleModalClose}
            className="bg-[#F2F2F7] rounded-full p-1 w-12 h-12 ml-5 flex items-center justify-center"
          >
            <IconX size={20} />
          </button>
        </div>
      )}

      <div className="flex-1 overflow-hidden px-6 pt-4 bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] rounded-tr-3xl">

          <div className="h-full overflow-y-auto space-y-4 pb-4">

            <div className="col-span-full border-[1.5px] border-[#D2D2D2] p-3 rounded-lg bg-white/30">

              <table className="w-full">

                <thead>

                  <tr className="border-b border-[#D8D8D8] border-dashed text-left">

                    <th className="p-2 text-sm font-medium text-[#212121]/70 text-center w-12">

                      #

                    </th>

                    <th className="p-2 text-sm font-medium text-[#212121]/70">

                      SKU (Product Name)

                    </th>

                    <th className="p-2 text-sm font-medium text-[#212121]/70 text-center">

                      Inv. Type

                    </th>

                    <th className="p-2 text-sm font-medium text-[#212121]/70 text-center">
                      {anyRowHasQuantity ? "Qty" : ""}
                    </th>

                    <th className="p-2 text-sm font-medium text-[#212121]/70">
                      {anyRowHasLocation ? "Scan Location" : ""}
                    </th>

                    <th className="p-2 text-sm font-medium text-[#212121]/70 text-center">

                      Action

                    </th>

                  </tr>

                </thead>

                <tbody>

                  {fields.map((field, i) => {

                    const isIdentified =

                      !!watchedProducts[i]?.productId && !isVerifyingSku[i];

                    const hasWarning = capacityWarnings[i];

                    const showQuantityInput =

                      watchedProducts[i]?.inventoryType === "reserved" ||

                      (watchedProducts[i]?.inventoryType === "received" && !!watchedProducts[i]?.scannedLocation);

                    const showLocationInput =

                      watchedProducts[i]?.inventoryType === "received" ||

                      (watchedProducts[i]?.inventoryType === "reserved" && scanningSteps[i] === "location");



                    return (

                      <tr

                        key={field.id}

                        className="hover:bg-[#FBD037]/20 transition border-b border-gray-100 last:border-0"

                      >

                        <td className="p-3 text-center align-top">{i + 1}</td>



                        {/* 1. Scan SKU */}

                        <td className="p-3 align-top relative">

                          <input

                            type="text"

                            {...register(`products.${i}.sku`)}

                            autoComplete="off"

                            placeholder="Scan SKU..."

                            className={`rounded-full w-56 px-4 py-1.5 text-sm border focus:outline-none transition-all ${watchedProducts[i]?.sku ? "border-green-300 bg-green-50/30" : "border-[#D8D8D8]"

                              } focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100`}

                            onFocus={() => {

                              setActiveSkuIndex(i);

                              // Only open dropdown if not in location step (not completed)

                              if (scanningSteps[i] !== "location") {

                                setShowSkuDropdown((p) => {

                                  const n = [...p];

                                  n[i] = true;

                                  return n;

                                });

                              }

                            }}

                            onBlur={() =>

                              setTimeout(() => {

                                setActiveSkuIndex(null);

                                setShowSkuDropdown((p) => {

                                  const n = [...p];

                                  n[i] = false;

                                  return n;

                                });

                              }, 200)

                            }

                            onKeyDown={(e) =>

                              e.key === "Enter" &&

                              handleVerifySku(e.currentTarget.value, i)

                            }

                          />

                          {showSkuDropdown[i] && (

                            <div className="absolute left-0 top-[calc(100%-10px)] w-80 max-h-60 overflow-y-auto bg-white border border-gray-100 rounded-xl shadow-[0_12px_40px_-10px_rgba(0,0,0,0.2)] z-[110] py-0 animate-in fade-in slide-in-from-top-2 duration-200 custom-scrollbar">

                              <div className="sticky top-0 bg-white px-4 py-2 flex items-center justify-between border-b border-gray-50 mb-1 z-10">

                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">

                                  Manifest Products

                                </span>

                                <span className="text-[10px] bg-yellow-50 text-yellow-700 px-1.5 py-0.5 rounded-md font-bold">

                                  {data?.products?.filter((p: any) => !p?.isAddedToInventory)?.filter((p: any) =>

                                    p.sku

                                      .toLowerCase()

                                      .includes(

                                        watch(`products.${i}.sku`)?.toLowerCase() || "",

                                      ),

                                  ).filter((product: any) => {

                                    // Check if this SKU has already been processed

                                    const processedRows = watchedProducts.filter((wp) =>

                                      wp.sku === product.sku &&

                                      (wp.inventoryType === "received" || wp.inventoryType === "reserved")

                                    );



                                    // SKU is available if its processed quantity is less than total

                                    const quantity = Number(product.receivedQty) || 0;

                                    const processedQty = processedRows.reduce((sum, wp) => sum + (Number(wp.quantity) || 0), 0);

                                    return processedQty < quantity && quantity > 0;

                                  })?.length || 0} found

                                </span>

                              </div>

                              {data?.products

                                ?.filter((p: any) => !p?.isAddedToInventory)

                                ?.filter((p: any) =>

                                  p.sku

                                    .toLowerCase()

                                    .includes(

                                      watch(`products.${i}.sku`)?.toLowerCase() || "",

                                    ),

                                )

                                ?.filter((product: any) => {

                                  // Check if this SKU has already been processed

                                  const processedRows = watchedProducts.filter((wp) =>

                                    wp.sku === product.sku &&

                                    (wp.inventoryType === "received" || wp.inventoryType === "reserved")

                                  );



                                  // SKU is available if its processed quantity is less than total

                                  const quantity = Number(product.receivedQty) || 0;

                                  const processedQty = processedRows.reduce((sum, wp) => sum + (Number(wp.quantity) || 0), 0);

                                  return processedQty < quantity && quantity > 0;

                                })

                                ?.map((product: any) => (

                                  <button

                                    key={product.productId}

                                    type="button"

                                    className="w-full text-left px-4 py-3 hover:bg-yellow-50 group transition-all flex items-center justify-between border-b border-gray-50 last:border-0"

                                    onMouseDown={(e) => {

                                      e.preventDefault();

                                      handleSkuSelection(product.sku, i, product);

                                    }}

                                  >

                                    <div className="flex flex-col">

                                      <span className="text-sm font-bold text-gray-700 group-hover:text-yellow-700 transition-colors">

                                        {product.sku}

                                      </span>

                                      <span className="text-[11px] text-gray-500 font-medium">

                                        {product.productName}

                                      </span>

                                      <div className="flex gap-2 mt-1">

                                        <span className="text-[10px] text-green-600 font-bold">Qty: {product.receivedQty || 0}</span>

                                      </div>

                                    </div>

                                    <IconCheck

                                      size={16}

                                      className="opacity-0 group-hover:opacity-100 text-yellow-500 transition-opacity"

                                    />

                                  </button>

                                ))}

                            </div>

                          )}

                          {isIdentified && (

                            <p className="text-[10px] text-gray-400 mt-1 flex items-center gap-1 overflow-hidden whitespace-nowrap text-ellipsis max-w-[220px]">

                              <IconCheck size={12} className="text-green-500 flex-shrink-0" />{" "}

                              {watchedProducts[i]?.productName}

                            </p>

                          )}

                        </td>



                        {/* 2. Select Type */}

                        <td className="p-3 align-top text-center">

                          {(() => {

                            const isChild = distributedFromIndex[i] >= 0;

                            const hasChildren = distributedFromIndex.some((d) => d === i);

                            const locationLocked = scanningSteps[i] === "item";

                            const isTypeLocked = isChild || hasChildren || locationLocked;



                            // Check if this SKU has already been processed with opposite type

                            const currentSku = watchedProducts[i]?.sku;

                            const skuProcessedWithOppositeType = watchedProducts.some((p, idx) =>

                              idx !== i &&

                              p.sku === currentSku &&

                              p.inventoryType === (watchedProducts[i]?.inventoryType === "received" ? "reserved" : "received")

                            );



                            const currentType = watchedProducts[i]?.inventoryType;



                            return (

                              <div className="flex flex-col gap-1 items-center justify-center h-full">

                                {(!currentType || currentType === "received") && (

                                  <button

                                    type="button"

                                    disabled={!watchedProducts[i]?.productId || isTypeLocked || skuProcessedWithOppositeType || !!currentType}

                                    onClick={() => handleTypeSelection("received", i)}

                                    className={`w-20 py-1 rounded-lg text-[10px] font-black tracking-wide border transition-all ${currentType === "received"

                                      ? "bg-[#FBD037] border-[#FBD037] text-black shadow-sm cursor-default"

                                      : "bg-white border-gray-200 text-gray-400 hover:border-yellow-200 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"

                                      }`}

                                  >

                                    RECEIVED

                                  </button>

                                )}

                                {(!currentType || currentType === "reserved") && (

                                  <button

                                    type="button"

                                    disabled={!watchedProducts[i]?.productId || isTypeLocked || skuProcessedWithOppositeType || !!currentType}

                                    onClick={() => handleTypeSelection("reserved", i)}

                                    className={`w-20 py-1 rounded-lg text-[10px] font-bold border transition-all ${currentType === "reserved"

                                      ? "bg-slate-800 border-slate-800 text-white shadow-sm cursor-default"

                                      : "bg-white border-gray-200 text-gray-400 hover:border-slate-300 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"

                                      }`}

                                  >

                                    RESERVED

                                  </button>

                                )}

                              </div>

                            );

                          })()}

                        </td>



                        {/* 3. Qty */}

                        <td className="p-2 align-top text-center">

                          {showQuantityInput ? (

                            <input

                              type="number"

                              disabled={!watchedProducts[i]?.inventoryType}

                              {...register(`products.${i}.quantity`, {

                                onChange: (e) => {

                                  const val = Number(e.target.value);

                                  const sku = watchedProducts[i]?.sku;

                                  const type = watchedProducts[i]?.inventoryType;

                                  if (!sku || !type || isNaN(val)) return;



                                  const matchedProduct = data?.products?.find((p: any) => p.sku === sku);

                                  if (!matchedProduct) return;



                                  const totalQty = Number(matchedProduct.receivedQty) || 0;



                                  let received = 0;

                                  let reserved = 0;



                                  watchedProducts.forEach((p, idx) => {

                                    if (p.sku === sku) {

                                      const pQty = idx === i ? val : (Number(p.quantity) || 0);

                                      if (p.inventoryType === "received") received += pQty;

                                      if (p.inventoryType === "reserved") reserved += pQty;

                                    }

                                  });



                                  if (val < 0) {

                                    toast.error(`Quantity cannot be less than zero`);

                                  } else if (received > totalQty) {

                                    toast.error(`RECEIVED quantity (${received}) cannot be more than total quantity (${totalQty}) for SKU ${sku}`);

                                  } else if (reserved > totalQty) {

                                    toast.error(`RESERVED quantity (${reserved}) cannot be more than total quantity (${totalQty}) for SKU ${sku}`);

                                  } else if (received + reserved > totalQty) {

                                    toast.error(`RECEIVED + RESERVED quantity (${received + reserved}) cannot be more than total quantity (${totalQty}) for SKU ${sku}`);

                                  }

                                }

                              })}

                              className="rounded-full text-center px-4 py-1.5 text-sm border border-[#D8D8D8] w-20 font-bold focus:border-yellow-400 focus:outline-none"

                              onKeyDown={(e) => {

                                if (e.key === "Enter") {

                                  e.preventDefault();

                                  const sku = watchedProducts[i]?.sku;

                                  const type = watchedProducts[i]?.inventoryType;

                                  if (sku && type) {

                                    handleVerifySku(sku, i, type);

                                  }

                                  if (type === "reserved") {

                                    setScanningSteps((p) => {

                                      const n = [...p];

                                      n[i] = "location";

                                      return n;

                                    });

                                  }

                                  setTimeout(() => setFocus(`products.${i}.scannedLocation`), 100);

                                }

                              }}

                            />

                          ) : (
                            null
                          )}

                        </td>



                        {/* 4. Scan Location */}

                        <td className="p-3 align-top relative">

                          {showLocationInput ? (

                            <>

                              <input

                                type="text"

                                disabled={!watchedProducts[i]?.inventoryType}

                                {...register(`products.${i}.scannedLocation`)}

                                autoComplete="off"

                                placeholder={watchedProducts[i]?.inventoryType ? "Scan Location..." : "Select type first"}

                                className={`rounded-full w-56 px-4 py-1.5 text-sm border focus:outline-none transition-all ${hasWarning

                                  ? "border-red-500 bg-red-50"

                                  : hasDuplicateLocations && watchedProducts[i]?.scannedLocation

                                    ? "border-orange-500 bg-orange-50"

                                    : "border-[#D8D8D8] focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100"

                                  } ${!watchedProducts[i]?.inventoryType ? "bg-gray-50 opacity-50 cursor-not-allowed" : ""}`}

                                onFocus={() => {

                                  if (!verifiedLocationIds[i]) {

                                    setActiveLocationIndex(i);

                                  }

                                }}

                                onBlur={() =>

                                  setTimeout(

                                    () => setActiveLocationIndex(null),

                                    200,

                                  )

                                }

                                onKeyDown={(e) => {

                                  if (e.key === "Enter") {

                                    const location = e.currentTarget.value;

                                    // Check for duplicate location before verifying

                                    const isDuplicate = watchedProducts.some((p, idx) =>

                                      idx !== i &&

                                      p.scannedLocation === location

                                    );



                                    if (isDuplicate) {

                                      toast.error("This location is already used for another SKU");

                                      return;

                                    }



                                    if (

                                      verifiedLocationIds[i] &&

                                      watchedProducts[i]?.scannedLocation === location &&

                                      i === fields.length - 1 &&

                                      !isFormDisabled &&

                                      !isPending &&

                                      !allCombinationsProcessed &&

                                      hasAvailableSkus

                                    ) {

                                      // Add new row

                                      append({

                                        productId: "",

                                        scannedLocation: "",

                                        sku: "",

                                        quantity: 0,

                                        expiryDate: "",

                                        hasExpiry: false,

                                        productName: "",

                                        inventoryType: undefined,

                                      });

                                      setScanningSteps((p) => [...p, "sku"]);

                                      setShowSkuDropdown((p) => [...p, false]);

                                      setIsVerifyingBarcode((p) => [...p, false]);

                                      setIsVerifyingSku((p) => [...p, false]);

                                      setCapacityWarnings((p) => [...p, ""]);

                                      setSuggestions((p) => [...p, null]);

                                      setPalletSuggestions((p) => [...p, null]);

                                      setUserChoice((p) => [...p, null]);

                                      setStorageMeta((p) => [...p, { binCap: 9999, palletCap: 9999, supportsBins: true, supportsPallets: true }]);

                                      setWarehouseOptions((p) => [...p, null]);

                                      setSelectedWarehouseIds((p) => [...p, null]);

                                      setDistributedFromIndex((p) => [...p, -1]);

                                      setVerifiedLocationIds((p) => [...p, null]);

                                      return;

                                    }



                                    handleVerifyBarcode(location, i);

                                  }

                                }}

                              />



                              {warehouseOptions[i] && warehouseOptions[i]!.length > 0 && (

                                <div className="absolute left-3 top-[calc(100%-6px)] w-64 bg-white border border-amber-200 rounded-xl shadow-xl z-[120] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">

                                  <div className="sticky top-0 bg-amber-50 px-4 py-2 flex items-center justify-between border-b border-amber-100">

                                    <span className="text-[10px] font-bold text-amber-700 uppercase tracking-widest">Select Warehouse</span>

                                    <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-md font-bold">{warehouseOptions[i]!.length}</span>

                                  </div>

                                  {warehouseOptions[i]!.map((wh) => (

                                    <button

                                      key={wh.warehouseId}

                                      type="button"

                                      className="w-full text-left px-4 py-3 hover:bg-amber-50 group transition-all flex items-center justify-between border-b border-gray-50 last:border-0"

                                      onMouseDown={(e) => {

                                        e.preventDefault();

                                        handleWarehouseSelect(wh.warehouseId, i);

                                      }}

                                    >

                                      <span className="text-sm font-semibold text-gray-700 group-hover:text-amber-700 transition-colors">{wh.warehouseName}</span>

                                      <IconCheck size={14} className="opacity-0 group-hover:opacity-100 text-amber-500 transition-opacity" />

                                    </button>

                                  ))}

                                </div>

                              )}



                              {hasWarning ? (

                                <div className="mt-2 p-3 bg-white border-2 border-red-200 rounded-xl shadow-xl w-80 z-[120]">

                                  {confirmingPallet === i ? (

                                    <div className="bg-blue-50 p-2 rounded-lg border border-blue-100">

                                      <p className="text-[10px] font-bold text-blue-800 mb-2">

                                        Capacity exceeded. Add to Pallet instead?

                                      </p>

                                      <div className="flex gap-2">

                                        <button

                                          type="button"

                                          onClick={() => {

                                            setUserChoice((p) => {

                                              const n = [...p];

                                              n[i] = true;

                                              return n;

                                            });

                                            setConfirmingPallet(null);

                                          }}

                                          className="flex-1 py-1 bg-blue-600 text-white rounded text-[10px] font-bold"

                                        >

                                          Yes (Pallet)

                                        </button>

                                        <button

                                          type="button"

                                          onClick={() => {

                                            setUserChoice((p) => {

                                              const n = [...p];

                                              n[i] = false;

                                              return n;

                                            });

                                            setConfirmingPallet(null);

                                          }}

                                          className="flex-1 py-1 bg-white border border-gray-300 rounded text-[10px] font-bold"

                                        >

                                          No (Split Bins)

                                        </button>

                                      </div>

                                    </div>

                                  ) : (

                                    <div className="flex flex-col gap-2 pt-2 border-t">

                                      <div className="flex justify-between items-center">

                                        <p className="text-[9px] font-bold text-gray-400 uppercase">

                                          Suggested{" "}

                                          {userChoice[i] ? "Pallets" : "Bins"}:

                                        </p>

                                        <button

                                          type="button"

                                          onClick={() => setConfirmingPallet(i)}

                                          className="text-blue-500"

                                        >

                                          <IconArrowBackUp size={14} />

                                        </button>

                                      </div>

                                      <div className="flex flex-wrap gap-1.5">

                                        {(userChoice[i]

                                          ? palletSuggestions[i]

                                          : suggestions[i]

                                        )

                                          ?.slice(0, 4)

                                          .map((loc) => {

                                            const cap = userChoice[i]

                                              ? storageMeta[i]?.palletCap

                                              : storageMeta[i]?.binCap;

                                            const left =

                                              cap - loc.currentInventoryCount;

                                            return (

                                              <span

                                                key={loc.barcodeLabel}

                                                className="px-2 py-0.5 bg-gray-100 text-[10px] font-bold rounded border border-gray-200"

                                              >

                                                {loc.barcodeLabel}{" "}

                                                <span className="text-blue-600">

                                                  ({left} left)

                                                </span>

                                              </span>

                                            );

                                          })}

                                      </div>

                                      <button

                                        type="button"

                                        onClick={() => handleBulkSplit(i)}

                                        className="w-full mt-2 py-1.5 bg-yellow-400 text-black rounded-lg text-[10px] font-bold flex items-center justify-center gap-1"

                                      >

                                        <IconLayersIntersect size={12} /> Auto-Distribute

                                      </button>

                                    </div>

                                  )}

                                </div>

                              ) : (

                                activeLocationIndex === i && (() => {

                                  const isInitialReceived =

                                    watchedProducts[i]?.inventoryType === "received" &&

                                    !watchedProducts[i]?.scannedLocation;



                                  const filteredBins = (suggestions[i] || []).filter((loc: any) =>

                                    loc.barcodeLabel

                                      ?.toLowerCase()

                                      .includes(watch(`products.${i}.scannedLocation`)?.toLowerCase() || "")

                                  );

                                  const filteredPallets = (palletSuggestions[i] || []).filter((loc: any) =>

                                    loc.barcodeLabel

                                      ?.toLowerCase()

                                      .includes(watch(`products.${i}.scannedLocation`)?.toLowerCase() || "")

                                  );



                                  const binSuggestionsToShow = isInitialReceived

                                    ? filteredBins.slice(0, 1)

                                    : filteredBins;



                                  const palletSuggestionsToShow = isInitialReceived

                                    ? (binSuggestionsToShow.length > 0 ? [] : filteredPallets.slice(0, 1))

                                    : filteredPallets;



                                  const totalSuggestionsCount = binSuggestionsToShow.length + palletSuggestionsToShow.length;



                                  return (

                                    <div className="absolute left-3 top-[calc(100%-10px)] w-64 max-h-60 overflow-y-auto bg-white border border-gray-100 rounded-xl shadow-[0_12px_40px_-10px_rgba(0,0,0,0.2)] z-[110] py-0 animate-in fade-in slide-in-from-top-2 duration-200 custom-scrollbar">

                                      <div className="sticky top-0 bg-white px-4 py-2 flex items-center justify-between border-b border-gray-50 mb-1 z-10">

                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">

                                          Suggested Locations

                                        </span>

                                        <span className="text-[10px] bg-yellow-50 text-yellow-700 px-1.5 py-0.5 rounded-md font-bold">

                                          {totalSuggestionsCount} found

                                        </span>

                                      </div>

                                      {/* Bin Suggestions */}

                                      {binSuggestionsToShow.length > 0 && (

                                        <>

                                          <div className="px-4 py-1 bg-gray-50 text-[9px] font-bold text-gray-400 uppercase tracking-widest border-b">Bins</div>

                                          {binSuggestionsToShow.map((loc: any) => (

                                            <button

                                              key={loc.barcodeLabel}

                                              type="button"

                                              className="w-full text-left px-4 py-3 hover:bg-yellow-50 group transition-all flex items-center justify-between border-b border-gray-50 last:border-0"

                                              onMouseDown={(e) => {

                                                e.preventDefault();

                                                // Check for duplicate location before setting

                                                const isDuplicate = watchedProducts.some((p, idx) =>

                                                  idx !== i &&

                                                  p.scannedLocation === loc.barcodeLabel

                                                );



                                                if (isDuplicate) {

                                                  toast.error("This location is already used for another SKU");

                                                  return;

                                                }



                                                setValue(`products.${i}.scannedLocation`, loc.barcodeLabel);

                                                handleVerifyBarcode(loc.barcodeLabel, i, loc.warehouseId, loc.id);

                                                setActiveLocationIndex(null);

                                              }}

                                            >

                                              <div className="flex flex-col">

                                                <span className="text-sm font-bold text-gray-700 group-hover:text-yellow-700 transition-colors">

                                                  {loc.barcodeLabel}

                                                </span>

                                                <span className="text-[10px] text-gray-400">{loc.currentInventoryCount} items stored</span>

                                              </div>

                                            </button>

                                          ))}

                                        </>

                                      )}

                                      {/* Pallet Suggestions */}

                                      {palletSuggestionsToShow.length > 0 && (

                                        <>

                                          <div className="px-4 py-1 bg-gray-50 text-[9px] font-bold text-gray-400 uppercase tracking-widest border-b">Pallets</div>

                                          {palletSuggestionsToShow.map((loc: any) => (

                                            <button

                                              key={loc.barcodeLabel}

                                              type="button"

                                              className="w-full text-left px-4 py-3 hover:bg-yellow-50 group transition-all flex items-center justify-between border-b border-gray-50 last:border-0"

                                              onMouseDown={(e) => {

                                                e.preventDefault();

                                                // Check for duplicate location before setting

                                                const isDuplicate = watchedProducts.some((p, idx) =>

                                                  idx !== i &&

                                                  p.scannedLocation === loc.barcodeLabel

                                                );



                                                if (isDuplicate) {

                                                  toast.error("This location is already used for another SKU");

                                                  return;

                                                }



                                                setValue(`products.${i}.scannedLocation`, loc.barcodeLabel);

                                                handleVerifyBarcode(loc.barcodeLabel, i, loc.warehouseId, loc.id);

                                                setActiveLocationIndex(null);

                                              }}

                                            >

                                              <div className="flex flex-col">

                                                <span className="text-sm font-bold text-gray-700 group-hover:text-yellow-700 transition-colors">

                                                  {loc.barcodeLabel}

                                                </span>

                                                <span className="text-[10px] text-gray-400">{loc.currentInventoryCount} items stored</span>

                                              </div>

                                            </button>

                                          ))}

                                        </>

                                      )}

                                      {/* Fallback if no suggestions */}

                                      {totalSuggestionsCount === 0 && (

                                        <div className="px-4 py-6 text-center text-[11px] text-gray-400">No suggestions available. Scan a location manually.</div>

                                      )}

                                    </div>

                                  );

                                })()

                              )}

                            </>

                          ) : (
                            null
                          )}

                        </td>







                        <td className="p-3 align-top text-center">

                          <button

                            type="button"

                            onClick={() => handleClearRow(i)}

                            className="p-1.5 text-gray-400 hover:text-red-500 transition-colors bg-gray-50 rounded-full hover:bg-red-50"

                          >

                            <IconRefresh size={18} />

                          </button>

                        </td>

                      </tr>

                    );

                  })}

                </tbody>

              </table>

            </div>

          </div>

        </div>

        <div className="flex items-center gap-4 justify-end p-6 rounded-b-[30px] bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC]">

          <button

            type="submit"

            disabled={isFormDisabled || isPending || !allCombinationsProcessed}

            className={`${isPending

              ? "bg-gray-800 cursor-not-allowed text-[#ffffff]"

              : "bg-[#FBD037] hover:bg-yellow-400 text-[#000000]"

              } flex items-center rounded-full p-3 px-8 space-x-2 disabled:cursor-not-allowed cursor-pointer text-base font-medium transition-all duration-200 shadow-md hover:shadow-lg`}

            onClick={() => console.log("DEBUG - Add Inventory button clicked, isFormDisabled:", isFormDisabled, "isPending:", isPending)}

          >

            Add Inventory

          </button>

          {/* <button

            type="button"

            disabled={isFormDisabled || isPending || allCombinationsProcessed || !hasAvailableSkus}

            className="flex items-center rounded-full p-3 px-4 text-sm border border-[#FBD037] bg-[#FBD037] text-[#000000] hover:bg-yellow-400 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg"

            onClick={() => {

              // Always add an empty row - user will select SKU from dropdown

              append({

                productId: "",

                scannedLocation: "",

                sku: "",

                quantity: 0,

                expiryDate: "",

                hasExpiry: false,

                productName: "",

                inventoryType: undefined,

              });

              setScanningSteps((p) => [...p, "sku"]);

              setShowSkuDropdown((p) => [...p, false]);

              setIsVerifyingBarcode((p) => [...p, false]);

              setIsVerifyingSku((p) => [...p, false]);

              setCapacityWarnings((p) => [...p, ""]);

              setSuggestions((p) => [...p, null]);

              setPalletSuggestions((p) => [...p, null]);

              setUserChoice((p) => [...p, null]);

              setStorageMeta((p) => [...p, { binCap: 9999, palletCap: 9999, supportsBins: true, supportsPallets: true }]);

              setWarehouseOptions((p) => [...p, null]);

              setSelectedWarehouseIds((p) => [...p, null]);

              setDistributedFromIndex((p) => [...p, -1]);

              setVerifiedLocationIds((p) => [...p, null]);

            }}

          >

            <IconPlus size={18} />

          </button> */}

        </div>
      </form>
  );

  if (isSidebar) {
    return formContent;
  }

  return (
    <div className="fixed top-0 left-0 w-full h-screen bg-black/60 z-50 flex items-center justify-end p-4">
      {formContent}
    </div>
  );

};



export default AddProductInventoryModal;

